import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Button,
  Alert,
  Typography,
  Paper
} from '@mui/material';
import { toast } from 'react-toastify';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useForm } from 'react-hook-form';
import ClientDropdown from 'src/content/timsheetReports/components/common/ClientDropdown';
import ProjectDropdownFilter from 'src/content/timsheetReports/components/common/ProjectDropdownFilter';
import ResourceDropdownFilter from 'src/content/timsheetReports/components/common/ResourceDropdownFilter';
import TimeEntryView from 'src/content/timeEntry/components/time-entry-view';
import { getRolePermissions } from 'src/services/authService';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';
import { getEmployeeById } from 'src/services/employeeService';
import moment from 'moment';

const EmployeeListTable = () => {
  const [filter, setFilter] = useState({
    customerIds: [],
    projectIds: [],
    employeeIds: [],
    employeeInfo: [] as { id: string; name: string }[],
    datePeriod: {
      fromDate: null as string | null,
      toDate: null as string | null
    }
  });
  const [toggleFilter, setToggleFilter] = useState(false);
  const [showTimeEntry, setShowTimeEntry] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { register, handleSubmit, setValue } = useForm();

  const handleClientChange = (selected: string) => {
    setFilter((prev) => ({
      ...prev,
      customerIds: selected ? [selected] : [],
      projectIds: [],
      employeeIds: [],
      employeeInfo: []
    }));
    setShowTimeEntry(false);
    setErrorMessage('');
    setValue('projectIds', []);
    setValue('employeeIds', []);
  };

  const handleProjectChange = (selected: string) => {
    setFilter((prev) => ({
      ...prev,
      projectIds: selected ? [selected] : [],
      employeeIds: [],
      employeeInfo: []
    }));
    setShowTimeEntry(false);
    setErrorMessage('');
    setValue('employeeIds', []);
  };

  const handleEmployeeChange = async (selected: string) => {
    if (selected && selected.trim() !== '') {
      const selectedIds = selected.split(',').filter((id) => id.trim() !== '');

      const employeeInfoArray = [];

      for (const id of selectedIds) {
        try {
          const response = await getEmployeeById(id.trim());
          const employee = response.data;
          const name = employee.name || id;
          employeeInfoArray.push({ id: id.trim(), name });
        } catch (error) {
          toast.error(
            `Error fetching employee details for ID ${id}: ${error.message}`
          );
          employeeInfoArray.push({ id: id.trim(), name: id.trim() });
        }
      }

      setFilter((prev) => ({
        ...prev,
        employeeIds: selectedIds,
        employeeInfo: employeeInfoArray
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        employeeIds: [],
        employeeInfo: []
      }));
    }
    setShowTimeEntry(false);
    setErrorMessage('');
  };

  const resetFilterResult = () => {
    setFilter({
      customerIds: [],
      projectIds: [],
      employeeIds: [],
      employeeInfo: [],
      datePeriod: { fromDate: null, toDate: null }
    });
    setShowTimeEntry(false);
    setErrorMessage('');
    setValue('customerIds', []);
    setValue('projectIds', []);
    setValue('employeeIds', []);
  };

  const onSubmit = () => {
    setErrorMessage('');

    if (filter.employeeIds.length === 0) {
      setErrorMessage(
        'Please select at least one employee to view their timesheet.'
      );
      setShowTimeEntry(false);
      return;
    }

    if (filter.employeeIds.length > 0 && filter.employeeInfo.length > 0) {
      setShowTimeEntry(true);
      setErrorMessage('');
    } else {
      setErrorMessage(
        'Employee information is missing. Please reselect the employees.'
      );
      setShowTimeEntry(false);
    }
  };

  const InlineTimeEntry = ({
    employee
  }: {
    employee: { id: string; name: string };
  }) => {
    const [permissions, setPermissions] = useState([]);

    const getPermissions = (mail) => {
      getRolePermissions(mail).then((res) => {
        const tabs = validateMultiRolesAndPermissions(res);
        const arr = tabs.filter((fil) => fil.name === 'Resource Pool');
        setPermissions(arr[0].permissions);
      });
    };

    useEffect(() => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (Object.keys(user).length > 0) {
        getPermissions(user.Email);
      }
    }, []);

    return (
      <TimeEntryView
        tab={undefined}
        permissions={permissions}
        id={employee.id}
        employeeInfo={employee}
      />
    );
  };

  return (
    <Box
      className="employee-list-table"
      sx={{
        width: '100%',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 2 }
      }}
    >
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
        <Grid
          sx={{
            px: { xs: 2, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: { xs: 2, sm: 0 }
          }}
          item
          xs={12}
        >
          <Button
            id="btn-setToggleFilter"
            sx={{
              color: `${!toggleFilter && 'gray'}`,
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: '100px', md: '120px' },
              height: { xs: '2.25rem', sm: '1.75rem' },
              fontSize: { xs: '0.875rem', sm: '12px' },
              fontWeight: 500,
              px: { xs: 2, sm: 1.5 },
              borderRadius: { xs: 1.5, sm: 1 },
              textTransform: 'none',
              '& .MuiButton-startIcon': {
                '& svg': {
                  fontSize: { xs: '18px', sm: '15px' }
                }
              }
            }}
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setToggleFilter(!toggleFilter)}
          >
            Filters
          </Button>
        </Grid>
        {toggleFilter && (
          <Grid
            px={{ xs: 2, sm: 2, md: 3 }}
            pb={{ xs: 2, sm: 2, md: 3 }}
            style={{ margin: 0 }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={{ xs: 2, sm: 2, md: 3 }}
                alignItems="flex-end"
                justifyContent="space-between"
                style={{
                  paddingTop: 2,
                  paddingBottom: 2
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={4}
                  sx={{
                    display: 'flex',
                    width: '100%'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ClientDropdown
                      title="Client"
                      {...register('customerIds')}
                      value={filter.customerIds.join(',') || null}
                      onChange={handleClientChange}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={4}
                  sx={{
                    display: 'flex',
                    width: '100%'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ProjectDropdownFilter
                      title="Project"
                      {...register('projectIds')}
                      value={filter.projectIds.join(',') || null}
                      projectId={filter.projectIds.join(',') || ''}
                      customerIds={filter.customerIds || []}
                      onChange={handleProjectChange}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={4}
                  sx={{
                    display: 'flex',
                    width: '100%'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ResourceDropdownFilter
                      title="Employee"
                      {...register('employeeIds')}
                      value={filter.employeeIds.join(',') || ''}
                      customerIds={filter.customerIds || []}
                      projectIds={filter.projectIds || []}
                      startDate={
                        filter?.datePeriod?.fromDate
                          ? moment(filter.datePeriod.fromDate).format(
                              'YYYY-MM-DD'
                            )
                          : null
                      }
                      endDate={
                        filter?.datePeriod?.toDate
                          ? moment(filter.datePeriod.toDate).format(
                              'YYYY-MM-DD'
                            )
                          : null
                      }
                      onChange={handleEmployeeChange}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} mt={{ xs: 3, sm: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 1.5, md: 2 },
                      justifyContent: { xs: 'stretch', sm: 'flex-end' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      width: '100%'
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={resetFilterResult}
                      id="btn-resetFilterResult"
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: 'auto', sm: '100px', md: '110px' },
                        height: { xs: '2.75rem', sm: '2rem', md: '2.25rem' },
                        fontSize: { xs: '0.9rem', sm: '0.75rem', md: '0.8rem' },
                        fontWeight: 500,
                        px: { xs: 2, sm: 1, md: 1.25 },
                        borderRadius: { xs: 2, sm: 1 },
                        textTransform: 'none'
                      }}
                      disabled={
                        filter.customerIds.length === 0 &&
                        filter.projectIds.length === 0 &&
                        filter.employeeIds.length === 0
                      }
                    >
                      Clear Filters
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      id="btn-onSubmit"
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: 'auto', sm: '120px', md: '130px' },
                        height: { xs: '2.75rem', sm: '2rem', md: '2.25rem' },
                        fontSize: { xs: '0.9rem', sm: '0.75rem', md: '0.8rem' },
                        fontWeight: 500,
                        px: { xs: 2, sm: 1, md: 1.25 },
                        borderRadius: { xs: 2, sm: 1 },
                        textTransform: 'none'
                      }}
                      disabled={filter.employeeIds.length === 0}
                    >
                      View Timesheet{filter.employeeIds.length > 1 ? 's' : ''}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
      </Card>
      {errorMessage && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            mx: { xs: 1, sm: 0 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Empty State - No Employees Selected */}
      {!showTimeEntry && filter.employeeIds.length === 0 && (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed #e0e0e0',
            backgroundColor: '#fafafa'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <PersonSearchIcon
              sx={{
                fontSize: { xs: '3rem', sm: '4rem' },
                color: '#bdbdbd'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: '#757575',
                fontWeight: 500,
                fontSize: { xs: '1.2rem', sm: '1.5rem' }
              }}
            >
              No Employees Selected
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#9e9e9e',
                maxWidth: '500px',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Please use the filters above to select one or more employees to
              view their timesheets. You can filter by client, project, and then
              choose specific employees.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setToggleFilter(true)}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: '140px' },
                  height: { xs: '2.75rem', sm: '2.5rem' },
                  fontSize: { xs: '0.9rem', sm: '0.875rem' },
                  fontWeight: 500,
                  px: { xs: 3, sm: 2 },
                  borderRadius: { xs: 2, sm: 1 },
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                Open Filters
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {showTimeEntry && filter.employeeInfo.length > 0 && (
        <Box
          mt={{ xs: 2, sm: 3 }}
          sx={{
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {filter.employeeInfo.map((employee, index) => (
            <Card
              key={employee.id}
              sx={{
                p: { xs: 1, sm: 2, md: 3 },
                mb: 3,
                borderRadius: 2,
                boxShadow: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1, sm: 2 },
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    borderBottom: '2px solid #1976d2',
                    pb: { xs: 1, sm: 2 },
                    mb: { xs: 1, sm: 2 }
                  }}
                >
                  <Box>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: '1.3rem',
                        fontWeight: 600,
                        color: '#1976d2'
                      }}
                    >
                      {employee.name}'s Timesheet
                    </h3>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: { xs: 1, sm: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: '#1976d2',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      {index + 1} of {filter.employeeInfo.length}
                    </Box>
                  </Box>
                </Box>

                {/* TimeEntry Component Container */}
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'auto',
                    '& > *': {
                      width: '100%'
                    }
                  }}
                >
                  <InlineTimeEntry employee={employee} />
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EmployeeListTable;